// Sendebetrieb im Blick: Störungen, was lief und was kommt, Regeln und Benachrichtigungen.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Icon, errorText } from "./streaming-ui";
import { EqBars } from "./ui";
import type { ChannelAlert, NotificationKind, NotificationTarget, PlayoutLogEntry, PlayoutSettings, MeldeEreignis } from "../types";

const inputClass = "input mt-1.5";

function clock(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function since(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `seit ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  return `seit ${hours} Std. ${minutes % 60} Min.`;
}

/** Offene Störungen aller Kanäle; regelmäßig nachgeladen. */
export function useOpenAlerts(intervalMs = 20_000): { open: ChannelAlert[]; recent: ChannelAlert[]; reload: () => Promise<void> } {
  const [state, setState] = useState<{ open: ChannelAlert[]; recent: ChannelAlert[] }>({ open: [], recent: [] });
  const reload = useCallback(async () => {
    try {
      setState(await api.getAlerts());
    } catch {
      // Ohne Verbindung zur API zeigt die Seite ohnehin einen Fehler.
    }
  }, []);
  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), intervalMs);
    return () => clearInterval(t);
  }, [reload, intervalMs]);
  return { ...state, reload };
}

/** Roter Balken über jeder Seite, solange etwas nicht sendet. */
export function AlertBanner(): JSX.Element | null {
  const { open } = useOpenAlerts();
  if (open.length === 0) return null;
  const first = open[0]!;
  return (
    <Link
      to="/servers"
      className="group flex items-center gap-2.5 border-b border-red-700/30 bg-gradient-to-r from-red-600 to-red-500 px-4 py-1.5 text-[0.8rem] text-white shadow-sm transition-colors hover:from-red-700 hover:to-red-600 md:pl-6"
      role="alert"
    >
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
        <Icon name="alert" className="h-3 w-3" />
      </span>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold">Störung bei {first.channel_name}:</span> <span className="opacity-95">{first.message}</span>
        {open.length > 1 && <span className="ml-2 rounded-full bg-white/20 px-1.5 py-px text-[0.7rem] font-medium">(+{open.length - 1} weitere)</span>}
      </span>
      <span className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium group-hover:bg-white/25">
        Ansehen <Icon name="chevron" className="h-3 w-3" />
      </span>
    </Link>
  );
}

export function ChannelAlerts({ alerts }: { alerts: ChannelAlert[] }): JSX.Element | null {
  if (alerts.length === 0) return null;
  return (
    <div className="mt-3 space-y-1.5">
      {alerts.map((a) => (
        <div key={a.id} className="callout callout-danger items-start" role="alert">
          <Icon name="alert" className="mt-0.5 h-4 w-4" />
          <div className="min-w-0">
            <span className="font-semibold">Störung {since(a.started_at)}:</span> {a.message}
            {a.notified_at && <span className="ml-2 text-xs opacity-75">(gemeldet)</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  planner: "Automatik",
  reserve: "Reserve",
  live: "Live",
  jingle: "Jingle",
};

/** Was lief und was als Nächstes kommt. */
export function ProgramLog({ slug }: { slug: string }): JSX.Element {
  const [history, setHistory] = useState<PlayoutLogEntry[] | null>(null);
  const [next, setNext] = useState<PlayoutLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [h, np] = await Promise.all([api.getHistory(slug, 25), api.getNowPlaying(slug)]);
      setHistory(h);
      setNext(np.next ?? []);
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <div className="callout callout-danger">{error}</div>;
  if (!history) return <div className="text-sm text-muted">Lade …</div>;

  const reserveRecently = history.slice(0, 5).some((h) => h.source === "reserve");
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <div className="section-label mb-2">Was lief</div>
        {reserveRecently && (
          <div className="callout callout-warning mb-2 text-xs">
            Zuletzt lief die Reserve-Playlist: Die Engine hat die Suite kurz nicht erreicht (z. B. bei einem Neustart der API).
          </div>
        )}
        {history.length === 0 ? (
          <div className="rounded-lg border border-dashed border-default px-3 py-4 text-sm text-muted">Noch nichts gesendet.</div>
        ) : (
          <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-default bg-card">
            {history.map((h) => (
              <li key={h.id} className={`flex min-w-0 items-center gap-3 px-3 py-2 text-sm ${h.status === "playing" ? "bg-brand-primary/[0.06]" : ""}`}>
                <span className="w-11 flex-shrink-0 font-mono text-xs tabular-nums text-muted">{clock(h.started_at)}</span>
                {h.status === "playing" && <EqBars className="flex-shrink-0 text-brand-primary" />}
                <span className={`min-w-0 flex-1 truncate ${h.status === "playing" ? "font-semibold text-primary" : "text-secondary"}`}>
                  {[h.artist, h.title].filter(Boolean).join(" – ") || "ohne Titelangabe"}
                </span>
                <span className={`badge flex-shrink-0 ${h.source === "live" ? "badge-danger" : h.source === "reserve" ? "badge-warning" : h.source === "jingle" ? "badge-info" : "badge-neutral"}`}>{SOURCE_LABEL[h.source] ?? h.source}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="min-w-0">
        <div className="mb-2 flex items-center justify-between">
          <span className="section-label">Als Nächstes</span>
          <Link to={`/channels/${slug}/rundown`} className="text-xs font-medium text-brand-primary hover:underline">ganzer Sendeablauf</Link>
        </div>
        {next.length === 0 ? (
          <div className="rounded-lg border border-dashed border-default px-3 py-4 text-sm text-muted">Die Automatik wählt den nächsten Titel kurz vor Ende des laufenden.</div>
        ) : (
          <ol className="divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-default bg-card">
            {next.map((n, i) => (
              <li key={n.id} className="flex min-w-0 items-center gap-3 px-3 py-2 text-sm text-secondary">
                <span className="w-5 flex-shrink-0 text-xs font-semibold tabular-nums text-muted">{i + 1}</span>
                <span className="min-w-0 truncate">{[n.artist, n.title].filter(Boolean).join(" – ")}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

const LOUDNESS_PRESETS = [
  { value: -16, label: "Normal (empfohlen)" },
  { value: -14, label: "Kräftig" },
  { value: -18, label: "Ruhig" },
];

/** Regeln für die Automatik und den Stille-Wächter – mit Voreinstellungen, die ohne Zutun passen. */
export function PlayoutSettingsForm({ slug }: { slug: string }): JSX.Element {
  const [settings, setSettings] = useState<PlayoutSettings | null>(null);
  const [defaults, setDefaults] = useState<PlayoutSettings | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");

  useEffect(() => {
    api.getPlayoutSettings(slug).then((r) => {
      setSettings(r.settings);
      setDefaults(r.defaults);
    }).catch((err) => setState(errorText(err)));
  }, [slug]);

  if (!settings || !defaults) return <div className="text-sm text-muted">{state === "idle" ? "Lade …" : state}</div>;

  const set = (patch: Partial<PlayoutSettings>): void => {
    setSettings({ ...settings, ...patch });
    setState("idle");
  };
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setState("saving");
    try {
      const r = await api.savePlayoutSettings(slug, settings);
      setSettings(r.settings);
      setState("saved");
    } catch (err) {
      setState(errorText(err));
    }
  };
  const preset = LOUDNESS_PRESETS.find((p) => p.value === settings.loudness_target_lufs);

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block text-[0.8rem] font-medium text-secondary">
          Lautstärke aller Titel angleichen
          <select
            className={inputClass}
            value={preset ? String(preset.value) : "custom"}
            onChange={(e) => e.target.value !== "custom" && set({ loudness_target_lufs: Number(e.target.value) })}
          >
            {LOUDNESS_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
            {!preset && <option value="custom">Eigener Wert ({settings.loudness_target_lufs} LUFS)</option>}
          </select>
          <span className="field-hint block font-normal">Jeder Titel wird beim Hochladen gemessen und auf dieselbe Lautstärke gebracht.</span>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Stille-Wächter greift ein nach
          <div className="mt-1.5 flex items-center gap-2 font-normal">
            <input type="number" min={5} max={300} className="input !w-24 tabular-nums" value={settings.silence_seconds}
              onChange={(e) => set({ silence_seconds: Number(e.target.value) })} />
            <span>Sekunden Stille</span>
          </div>
          <span className="field-hint block font-normal">Dann springt die Automatik zum nächsten Titel bzw. übernimmt vom stummen Live-Eingang – und ihr bekommt eine Meldung.</span>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Gleicher Künstler frühestens nach
          <div className="mt-1.5 flex items-center gap-2 font-normal">
            <input type="number" min={0} max={1440} className="input !w-24 tabular-nums" value={settings.artist_separation_minutes}
              onChange={(e) => set({ artist_separation_minutes: Number(e.target.value) })} />
            <span>Minuten</span>
          </div>
        </label>
        <label className="block text-[0.8rem] font-medium text-secondary">
          Gleicher Titel frühestens nach
          <div className="mt-1.5 flex items-center gap-2 font-normal">
            <input type="number" min={0} max={10080} className="input !w-24 tabular-nums" value={settings.title_separation_minutes}
              onChange={(e) => set({ title_separation_minutes: Number(e.target.value) })} />
            <span>Minuten</span>
          </div>
          <span className="field-hint block font-normal">Reicht die Mediathek dafür nicht, lockert die Automatik die Regel – es wird nie still.</span>
        </label>
      </div>
      <button type="button" onClick={() => setAdvanced((a) => !a)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">
        <Icon name={advanced ? "up" : "down"} className="h-3.5 w-3.5" />
        {advanced ? "Erweitert ausblenden" : "Erweitert"}
      </button>
      {advanced && (
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-default bg-card p-4 sm:grid-cols-2">
          <label className="block text-[0.8rem] font-medium text-secondary">
            Ziel-Lautheit (LUFS)
            <input type="number" step={0.5} min={-24} max={-9} className={inputClass} value={settings.loudness_target_lufs}
              onChange={(e) => set({ loudness_target_lufs: Number(e.target.value) })} />
            <span className="field-hint block font-normal">Standard {defaults.loudness_target_lufs}. Werte näher an 0 sind lauter.</span>
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary">
            Überblendung für ungemessene Titel (ms)
            <input type="number" step={500} min={0} max={12000} className={inputClass} value={settings.crossfade_ms}
              onChange={(e) => set({ crossfade_ms: Number(e.target.value) })} />
            <span className="field-hint block font-normal">Gemessene Titel blenden an ihrem eigenen Ausklang über.</span>
          </label>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={state === "saving"} className="btn btn-primary">
          Speichern
        </button>
        {state === "saved" && <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Gespeichert. Gilt ab dem nächsten Titel, ohne Unterbrechung.</span>}
        {state !== "idle" && state !== "saving" && state !== "saved" && <span className="text-xs text-red-600 dark:text-red-400">{state}</span>}
      </div>
    </form>
  );
}

const KIND_LABEL: Record<NotificationKind, string> = { telegram: "Telegram", email: "E-Mail", webhook: "Webhook", matrix: "Matrix" };

/** Reihenfolge der Ereignisse in der Oberfläche - Störungen zuerst. */
const EREIGNIS_FOLGE: MeldeEreignis[] = ["stoerung", "posteingang", "tagesbericht"];

/** Was in der Zeile hinter dem Namen steht: knapp, aber eindeutig. */
function zielAdresse(t: NotificationTarget): string {
  switch (t.kind) {
    case "email": return t.config.to ?? "";
    case "telegram": return `Chat ${t.config.chat_id ?? ""}`;
    case "matrix": return `${t.config.room_id ?? ""} auf ${t.config.homeserver ?? ""}`;
    default: return t.config.url ?? "";
  }
}

/** Wohin Störungsmeldungen gehen. */
export function NotificationSettings(): JSX.Element {
  const [data, setData] = useState<{
    email_available: boolean;
    ereignisse: Record<MeldeEreignis, string>;
    targets: NotificationTarget[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.listNotificationTargets());
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  const act = async (fn: () => Promise<unknown>, success?: string): Promise<void> => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice(success);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm leading-relaxed text-secondary">
        Die Suite meldet sich, wenn ein Kanal nicht sendet, länger still ist oder ein Stream nicht beim Server ankommt –
        und noch einmal, wenn alles wieder läuft. Kurze Aussetzer von wenigen Sekunden lösen keine Meldung aus.
      </p>
      <p className="max-w-3xl text-sm leading-relaxed text-muted">
        Jedes Ziel bekommt nur das, was ihr ihm gebt. Störungen sind die Voreinstellung. Wer auch den Posteingang
        bestellt, hört von Kontaktanfragen, Terminen und O-Tönen sofort – Musikwünsche und Zurufe ins Studio
        stehen nur im Tagesbericht, sonst piept es während jeder Sendung im Minutentakt.
      </p>
      {error && <div className="callout callout-danger">{error}</div>}
      {notice && <div className="callout callout-success">{notice}</div>}
      {!data ? (
        <div className="text-sm text-muted">Lade …</div>
      ) : (
        <>
          {data.targets.length === 0 && !adding && (
            <div className="callout callout-warning">
              Noch kein Ziel eingerichtet: Bei einer Störung erfährt es niemand, bis jemand in die Suite schaut.
            </div>
          )}
          <ul className="space-y-2">
            {data.targets.map((t) => (
              <li key={t.id} className={`flex flex-wrap items-center gap-2 rounded-lg border border-default bg-card p-3.5 ${t.enabled ? "" : "opacity-70"}`}>
                <span className="badge badge-brand">{KIND_LABEL[t.kind]}</span>
                <span className="font-medium text-primary">{t.name}</span>
                {!t.enabled && <span className="badge badge-neutral">pausiert</span>}
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-muted">{zielAdresse(t)}</span>
                {t.last_error ? (
                  <span className="badge badge-danger" title={t.last_error}>Letzter Versand fehlgeschlagen</span>
                ) : t.last_sent_at ? (
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">zuletzt gesendet {new Date(t.last_sent_at).toLocaleString("de-DE")}</span>
                ) : null}
                <button onClick={() => act(() => api.testNotificationTarget(t.id), `Testmeldung an „${t.name}" gesendet.`)} className="btn btn-secondary btn-sm">
                  Test senden
                </button>
                <button onClick={() => act(() => api.updateNotificationTarget(t.id, { enabled: !t.enabled }))} className="btn btn-ghost btn-sm">
                  {t.enabled ? "Pausieren" : "Einschalten"}
                </button>
                <button
                  onClick={() => window.confirm(`„${t.name}" löschen?`) && act(() => api.deleteNotificationTarget(t.id))}
                  className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                >
                  Löschen
                </button>
                {t.last_error && <div className="w-full text-xs text-red-600 dark:text-red-400">{t.last_error}</div>}
                <div className="mt-1 flex w-full flex-wrap items-center gap-3 border-t border-default pt-2.5 text-xs text-secondary">
                  <span className="text-muted">Meldet:</span>
                  {EREIGNIS_FOLGE.map((e) => {
                    const an = t.ereignisse.includes(e);
                    return (
                      <label key={e} className="flex cursor-pointer items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={an}
                          onChange={() => {
                            const naechste = an ? t.ereignisse.filter((x) => x !== e) : [...t.ereignisse, e];
                            // Ein Ziel ohne Ereignis wäre ein Ziel, das nie etwas
                            // schickt - dann lieber pausieren, das sagt es klarer.
                            if (naechste.length === 0) {
                              void act(() => api.updateNotificationTarget(t.id, { enabled: false }), `„${t.name}" pausiert – ein Ziel ohne Meldung wäre nur Zierde.`);
                              return;
                            }
                            void act(() => api.updateNotificationTarget(t.id, { ereignisse: naechste }));
                          }}
                        />
                        {data.ereignisse[e] ?? e}
                      </label>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
          {adding ? (
            <AddNotificationTarget emailAvailable={data.email_available} ereignisTexte={data.ereignisse} onDone={async (created) => { setAdding(false); if (created) await load(); }} />
          ) : (
            <button onClick={() => setAdding(true)} className="btn btn-secondary">
              <Icon name="plus" /> Ziel hinzufügen
            </button>
          )}
        </>
      )}
    </div>
  );
}

function AddNotificationTarget({ emailAvailable, ereignisTexte, onDone }: {
  emailAvailable: boolean;
  ereignisTexte: Record<MeldeEreignis, string>;
  onDone: (created: boolean) => Promise<void>;
}): JSX.Element {
  const [kind, setKind] = useState<NotificationKind>("telegram");
  const [name, setName] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [ereignisse, setEreignisse] = useState<MeldeEreignis[]>(["stoerung"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createNotificationTarget({ kind, name: name || KIND_LABEL[kind], config, ereignisse });
      // Gleich prüfen, ob es ankommt – sonst merkt man es erst bei der ersten Störung.
      await api.testNotificationTarget(created.id).catch(() => undefined);
      await onDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key: string, label: string, props: Record<string, unknown> = {}): JSX.Element => (
    <label className="block text-[0.8rem] font-medium text-secondary">
      {label}
      <input className={inputClass} value={config[key] ?? ""} onChange={(e) => setConfig({ ...config, [key]: e.target.value.trim() })} required {...props} />
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border border-default bg-card p-4 shadow-sm">
      <div className="tabbar flex-wrap">
        {(["telegram", "matrix", "email", "webhook"] as NotificationKind[]).map((k) => (
          <button
            key={k}
            type="button"
            disabled={k === "email" && !emailAvailable}
            onClick={() => { setKind(k); setConfig({}); }}
            className={`tab ${kind === k ? "tab-active" : ""} disabled:opacity-40`}
            title={k === "email" && !emailAvailable ? "E-Mail ist auf diesem Server nicht eingerichtet (SMTP_URL)." : undefined}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>
      <label className="block text-[0.8rem] font-medium text-secondary">
        Name
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "telegram" ? "z. B. Team-Gruppe" : kind === "email" ? "z. B. Technik" : kind === "matrix" ? "z. B. Raum Technik" : "z. B. Discord #technik"} />
      </label>
      <fieldset className="rounded-lg border border-default px-3 pb-3 pt-1 text-sm text-secondary">
        <legend className="px-1 text-xs font-medium text-muted">Wofür meldet dieses Ziel?</legend>
        {EREIGNIS_FOLGE.map((e) => (
          <label key={e} className="mr-4 inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={ereignisse.includes(e)}
              onChange={() => setEreignisse(ereignisse.includes(e) ? ereignisse.filter((x) => x !== e) : [...ereignisse, e])}
            />
            {ereignisTexte[e] ?? e}
          </label>
        ))}
      </fieldset>
      {kind === "telegram" && (
        <>
          <ol className="well list-decimal space-y-1 !pl-8 text-xs leading-relaxed text-secondary">
            <li>In Telegram <b>@BotFather</b> öffnen, <code>/newbot</code> schreiben und das Token kopieren.</li>
            <li>Dem neuen Bot eine Nachricht schreiben oder ihn in eure Gruppe holen.</li>
            <li>Chat-ID herausfinden, z. B. mit <b>@userinfobot</b> (Gruppen-IDs beginnen mit einem Minus).</li>
          </ol>
          {field("bot_token", "Bot-Token", { placeholder: "123456789:ABC…" })}
          {field("chat_id", "Chat-ID", { placeholder: "-1001234567890" })}
        </>
      )}
      {kind === "matrix" && (
        <>
          <ol className="well list-decimal space-y-1 !pl-8 text-xs leading-relaxed text-secondary">
            <li>Auf eurem Matrix-Server ein eigenes Konto anlegen, z. B. <b>radiobot</b>.</li>
            <li>Damit anmelden und das Zugangstoken kopieren (Element: Einstellungen → Hilfe &amp; Info → Erweitert).</li>
            <li>Den Bot in den Raum einladen und die interne Raumkennung kopieren (Raumeinstellungen → Erweitert).</li>
          </ol>
          {field("homeserver", "Heimserver", { type: "url", placeholder: "https://matrix.meinradio.de" })}
          {field("access_token", "Zugangstoken", { placeholder: "syt_…" })}
          {field("room_id", "Raumkennung", { placeholder: "!abcdef:matrix.meinradio.de" })}
          <p className="text-xs text-muted">
            Das Token gilt wie ein Passwort für dieses Konto – nimm dafür kein persönliches. Nach dem Speichern
            zeigt die Suite es nicht mehr an.
          </p>
        </>
      )}
      {kind === "email" && field("to", "An", { type: "email", placeholder: "technik@meinradio.de" })}
      {kind === "webhook" && (
        <>
          <p className="text-xs text-muted">
            Discord: Servereinstellungen → Integrationen → Webhooks. Slack: „Incoming Webhook“. Jede andere Adresse bekommt die Meldung als JSON.
          </p>
          {field("url", "Webhook-Adresse", { type: "url", placeholder: "https://discord.com/api/webhooks/…" })}
        </>
      )}
      {error && <div className="callout callout-danger">{error}</div>}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={busy || ereignisse.length === 0}
          title={ereignisse.length === 0 ? "Ohne Ereignis würde dieses Ziel nie etwas bekommen." : undefined}
          className="btn btn-primary"
        >
          {busy ? "Speichere …" : "Speichern und Test senden"}
        </button>
        <button type="button" onClick={() => void onDone(false)} className="btn btn-ghost">
          Abbrechen
        </button>
      </div>
    </form>
  );
}
